import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import {
  ChartLineUp, Lightning, Target, Warning, TrendUp, TrendDown,
  ShoppingBag, MagnifyingGlass, Tag, Pause, Play, ArrowsClockwise,
  Sparkle, CaretDown, CaretRight, Question, Gear, X,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtMoney = (n, c = "INR") => {
  if (!n && n !== 0) return "—";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n);
  } catch { return `${c} ${Math.round(n).toLocaleString("en-IN")}`; }
};
const fmtNum = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));
const fmtPct = (n) => (n == null ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`);

const PERIOD_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 15, label: "Last 15 days" },
  { value: 30, label: "Last 30 days" },
  { value: 60, label: "Last 60 days" },
  { value: 90, label: "Last 90 days" },
  { value: 180, label: "Last 6 months" },
];

const BUCKET_META = {
  scale:            { label: "Scale Now",                   color: "#10B981", icon: TrendUp,         priority: 1 },
  pause:            { label: "Pause / Reduce Budget",       color: "#EF4444", icon: Pause,           priority: 1 },
  fix_landing:      { label: "Fix Landing / Product Page",  color: "#F59E0B", icon: MagnifyingGlass, priority: 2 },
  tracking_issue:   { label: "Tracking Issue",              color: "#F97316", icon: Warning,         priority: 2 },
  refresh_creative: { label: "Creative Refresh Needed",     color: "#A855F7", icon: Sparkle,         priority: 3 },
  needs_mapping:    { label: "Needs Product Mapping",       color: "#06B6D4", icon: Tag,             priority: 3 },
  monitor:          { label: "Monitor",                     color: "#3B82F6", icon: Target,          priority: 4 },
};

const Movement = ({ pct, inverted = false }) => {
  if (pct == null || isNaN(pct)) return <span className="text-[10px] text-[#52525B]">—</span>;
  const isUp = pct >= 0;
  const isGood = inverted ? !isUp : isUp;
  const Icon = isUp ? TrendUp : TrendDown;
  const color = isGood ? "#86EFAC" : "#FF9999";
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px]" style={{ color }}>
      <Icon size={10} weight="bold" />
      {fmtPct(pct)}
    </span>
  );
};

const Section = ({ title, subtitle, icon: Icon, color, children, defaultOpen = true, testid, count }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card-flat overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#161618] text-left">
        {Icon && (
          <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: `${color}22` }}>
            <Icon size={18} weight="bold" style={{ color }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold flex items-center gap-2">
            {title}
            {count != null && (
              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${color}22`, color }}>{count}</span>
            )}
          </div>
          {subtitle && <div className="text-[10px] text-[#71717A]">{subtitle}</div>}
        </div>
        {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
      </button>
      {open && <div className="p-4 pt-0">{children}</div>}
    </div>
  );
};

// =================== Decision Summary Strip ===================
const DecisionSummary = ({ summary, currency, onRebuildMappings, rebuildingMappings }) => {
  if (!summary) return null;
  const wasteCount =
    (summary.bucket_counts?.pause || 0) +
    (summary.bucket_counts?.tracking_issue || 0) +
    (summary.bucket_counts?.fix_landing || 0);
  return (
    <div className="card-flat p-4 space-y-3" data-testid="decision-summary">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Lightning size={16} weight="fill" style={{ color: "#FFEB00" }} />
          Decision Summary
        </h3>
        <span className="text-[10px] text-[#71717A]">
          {summary.period_days}-day window · {summary.has_comparison ? "with previous period" : "no previous-period data"}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-[#0A0A0C] p-3 rounded">
          <div className="text-[10px] text-[#71717A] uppercase tracking-wider">Total Spend</div>
          <div className="text-lg font-bold data-number">{fmtMoney(summary.total_spend, currency)}</div>
          <div className="text-[10px] text-[#A1A1AA]">{fmtNum(summary.total_ads)} ads analysed</div>
        </div>
        <div className="bg-[#0A0A0C] p-3 rounded">
          <div className="text-[10px] text-[#71717A] uppercase tracking-wider">Shopify Revenue</div>
          <div className="text-lg font-bold data-number" style={{ color: "#86EFAC" }}>{fmtMoney(summary.total_revenue, currency)}</div>
          <div className="text-[10px] text-[#A1A1AA]">
            {fmtNum(summary.total_orders)} orders · {summary.ads_with_shopify_data || 0}/{summary.total_ads} ads tracked
          </div>
        </div>
        <div className="bg-[#0A0A0C] p-3 rounded">
          <div className="text-[10px] text-[#71717A] uppercase tracking-wider">Blended ROAS</div>
          <div className="text-lg font-bold data-number" style={{ color: summary.blended_roas >= 2 ? "#86EFAC" : summary.blended_roas >= 1 ? "#FCD34D" : "#FF9999" }}>
            {summary.blended_roas}×
          </div>
        </div>
        <div className="bg-[#0A0A0C] p-3 rounded">
          <div className="text-[10px] text-[#71717A] uppercase tracking-wider">Action Items</div>
          <div className="text-lg font-bold data-number">{summary.total_classified}</div>
          <div className="text-[10px] text-[#A1A1AA]">{summary.bucket_counts?.scale || 0} scale · {wasteCount} fix</div>
        </div>
      </div>

      {/* Biggest problem + best opportunity */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {summary.biggest_problem && (
          <div className="p-3 rounded" style={{ background: "#EF444411", border: "1px solid #EF444433" }}>
            <div className="flex items-center gap-2 mb-1">
              <Warning size={14} weight="fill" style={{ color: "#FF9999" }} />
              <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#FF9999" }}>Biggest Problem</span>
            </div>
            <div className="text-base font-bold">{summary.biggest_problem.label}: <span className="data-number" style={{ color: "#FF9999" }}>{summary.biggest_problem.value}</span></div>
            <div className="text-[11px] text-[#A1A1AA] mt-0.5">{summary.biggest_problem.detail}</div>
          </div>
        )}
        {summary.best_opportunity && (
          <div className="p-3 rounded" style={{ background: "#10B98111", border: "1px solid #10B98133" }}>
            <div className="flex items-center gap-2 mb-1">
              <TrendUp size={14} weight="fill" style={{ color: "#86EFAC" }} />
              <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#86EFAC" }}>Best Opportunity</span>
            </div>
            <div className="text-base font-bold">{summary.best_opportunity.label}: <span className="data-number" style={{ color: "#86EFAC" }}>{summary.best_opportunity.value}</span></div>
            <div className="text-[11px] text-[#A1A1AA] mt-0.5">{summary.best_opportunity.detail}</div>
          </div>
        )}
      </div>

      {/* Bucket pills */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {Object.entries(BUCKET_META)
          .sort((a, b) => a[1].priority - b[1].priority)
          .map(([key, meta]) => {
            const count = summary.bucket_counts?.[key] || 0;
            if (count === 0) return null;
            const Icon = meta.icon;
            return (
              <a key={key} href={`#bucket-${key}`} className="flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-semibold transition-opacity hover:opacity-80" style={{ background: `${meta.color}22`, color: meta.color }}>
                <Icon size={11} weight="bold" />
                {meta.label}
                <span className="bg-black/20 px-1 rounded">{count}</span>
              </a>
            );
          })}
      </div>
    </div>
  );
};

// =================== What Should I Do Today ===================
const WhatToDoToday = ({ summary, ads, currency }) => {
  if (!summary) return null;
  const priorities = ["pause", "scale", "fix_landing", "needs_mapping", "refresh_creative", "tracking_issue"];
  return (
    <div className="card-flat p-4 space-y-3" data-testid="what-to-do-today">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Lightning size={16} weight="fill" style={{ color: "#A855F7" }} />
          What Should I Do Today?
        </h3>
        <span className="text-[10px] text-[#71717A]">Top priority actions only</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {priorities.map((bucket) => {
          const meta = BUCKET_META[bucket];
          const count = summary.bucket_counts?.[bucket] || 0;
          if (count === 0) return null;
          const spend = summary.bucket_spend?.[bucket] || 0;
          const revenue = summary.bucket_revenue?.[bucket] || 0;
          const Icon = meta.icon;
          const sample = ads.filter((a) => a.classification?.bucket === bucket).slice(0, 2);
          const impactLabel =
            bucket === "pause" ? `Save ${fmtMoney(spend, currency)}` :
            bucket === "scale" ? `Already earning ${fmtMoney(revenue, currency)}` :
            bucket === "needs_mapping" ? `${fmtMoney(spend, currency)} spend untracked` :
            bucket === "tracking_issue" ? `${fmtMoney(spend, currency)} at risk of mis-attribution` :
            bucket === "fix_landing" ? `${fmtMoney(spend, currency)} click intent lost on PDP` :
            `${count} ad${count > 1 ? "s" : ""} affected`;
          return (
            <a
              key={bucket}
              href={`#bucket-${bucket}`}
              className="p-3 rounded transition-colors hover:bg-[#161618]"
              style={{ background: "#0A0A0C", borderLeft: `3px solid ${meta.color}` }}
              data-testid={`todo-${bucket}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <Icon size={14} weight="bold" style={{ color: meta.color }} />
                  <span className="text-xs font-semibold">{meta.label}</span>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: `${meta.color}22`, color: meta.color }}>
                  {count}
                </span>
              </div>
              <div className="text-[11px] data-number font-semibold" style={{ color: meta.color }}>{impactLabel}</div>
              {sample.length > 0 && (
                <div className="mt-1.5 text-[10px] text-[#A1A1AA] truncate">
                  e.g. {sample.map((a) => a.name).join(" · ")}{count > sample.length ? ` …+${count - sample.length} more` : ""}
                </div>
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
};

// =================== Ad Card ===================
const AdCard = ({ ad, currency, onAction, writeEnabled }) => {
  const meta = BUCKET_META[ad.classification?.bucket];
  const m = ad.metrics || {};
  const sh = ad.shopify || {};
  const Icon = meta?.icon;
  const isActive = ad.effective_status === "ACTIVE";
  return (
    <div className="card-flat p-3 space-y-2" data-testid="perf-ad-card" style={meta ? { borderLeft: `3px solid ${meta.color}` } : undefined}>
      <div className="flex gap-3">
        {/* Thumbnail */}
        <div className="w-20 h-20 shrink-0 rounded overflow-hidden bg-[#1F1F22] flex items-center justify-center">
          {ad.thumbnail_url ? (
            <img src={ad.thumbnail_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <Question size={20} className="text-[#52525B]" />
          )}
        </div>
        {/* Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-[#E4E4E7] line-clamp-2 leading-tight">{ad.name}</div>
              <div className="text-[10px] text-[#71717A] truncate mt-0.5">
                {ad.campaign_name} · {ad.adset_name}
              </div>
            </div>
            {meta && (
              <span className="shrink-0 text-[9px] px-1.5 py-0.5 rounded font-semibold flex items-center gap-1" style={{ background: `${meta.color}22`, color: meta.color }}>
                <Icon size={10} weight="bold" />
                {meta.label}
              </span>
            )}
          </div>
          {sh.product?.title && (
            <div className="mt-1 text-[10px] text-[#06B6D4] truncate">→ {sh.product.title}</div>
          )}
        </div>
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-[10px]">
        <Metric label="Spend" value={fmtMoney(m.spend, currency)} delta={ad.movement?.spend} />
        <Metric label="Revenue" value={fmtMoney(sh.revenue, currency)} color="#86EFAC" />
        <Metric label="ROAS" value={`${sh.roas || 0}×`} color={(sh.roas || 0) >= 2 ? "#86EFAC" : (sh.roas || 0) >= 1 ? "#FCD34D" : "#FF9999"} />
        <Metric label="CTR" value={`${m.ctr || 0}%`} delta={ad.movement?.ctr} />
        <Metric label="CPC" value={fmtMoney(m.cpc, currency)} delta={ad.movement?.cpc} invertedDelta />
        <Metric label="Orders" value={fmtNum(sh.orders)} />
      </div>

      {/* Reason + recommended action */}
      {ad.classification && (
        <div className="bg-[#0A0A0C] p-2 rounded text-[11px] space-y-1">
          <div><span className="text-[#71717A]">Why: </span><span className="text-[#E4E4E7]">{ad.classification.reason}</span></div>
          <div><span className="text-[#71717A]">Do: </span><span className="text-[#E4E4E7]">{ad.classification.action}</span></div>
        </div>
      )}

      {/* Quick actions */}
      <div className="flex items-center gap-2 pt-1">
        {writeEnabled && isActive && ad.classification?.bucket === "pause" && (
          <button
            onClick={() => onAction?.(ad, "pause")}
            className="text-[10px] px-2 py-1 rounded font-semibold flex items-center gap-1"
            style={{ background: "#EF444422", color: "#FF9999" }}
            data-testid="quick-pause-btn"
          >
            <Pause size={10} weight="bold" />
            Pause Ad
          </button>
        )}
        {writeEnabled && !isActive && ad.classification?.bucket === "scale" && (
          <button
            onClick={() => onAction?.(ad, "resume")}
            className="text-[10px] px-2 py-1 rounded font-semibold flex items-center gap-1"
            style={{ background: "#10B98122", color: "#86EFAC" }}
            data-testid="quick-resume-btn"
          >
            <Play size={10} weight="bold" />
            Resume Ad
          </button>
        )}
        <a
          href={`https://www.facebook.com/adsmanager/manage/ads?selected_ad_ids=${ad.id}`}
          target="_blank" rel="noreferrer"
          className="text-[10px] text-[#1877F2] hover:underline ml-auto"
        >
          Open in Meta →
        </a>
      </div>
    </div>
  );
};

const Metric = ({ label, value, color, delta, invertedDelta }) => (
  <div>
    <div className="text-[#71717A]">{label}</div>
    <div className="data-number font-semibold flex items-center gap-1" style={color ? { color } : undefined}>
      <span>{value}</span>
      {delta != null && <Movement pct={delta} inverted={invertedDelta} />}
    </div>
  </div>
);

// =================== Product Opportunities ===================
const ProductOpportunities = ({ data, currency }) => {
  if (!data) return null;
  const { untapped = [], wasting = [], under_supported = [] } = data;
  if (untapped.length === 0 && wasting.length === 0 && under_supported.length === 0) return null;
  return (
    <Section
      title="Product Opportunities"
      subtitle="Cross-references Meta ads with Shopify best sellers"
      icon={ShoppingBag}
      color="#10B981"
      testid="product-opportunities-section"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <OpportunityColumn title="Untapped Winners" subtitle="Selling organically · no ads" color="#10B981" items={untapped} currency={currency} cta="Create ads" />
        <OpportunityColumn title="Under-Supported" subtitle="High ROAS · low spend" color="#FFEB00" items={under_supported} currency={currency} cta="Scale budget" />
        <OpportunityColumn title="Wasting Spend" subtitle="High spend · low return" color="#EF4444" items={wasting} currency={currency} cta="Pause / fix" />
      </div>
    </Section>
  );
};

const OpportunityColumn = ({ title, subtitle, color, items, currency, cta }) => (
  <div className="space-y-2" style={{ borderTop: `2px solid ${color}` }}>
    <div className="pt-2">
      <div className="text-xs font-bold" style={{ color }}>{title}</div>
      <div className="text-[10px] text-[#71717A]">{subtitle}</div>
    </div>
    {items.length === 0 ? (
      <div className="text-[11px] text-[#52525B] py-2">None detected</div>
    ) : items.map((p, i) => (
      <div key={i} className="bg-[#0A0A0C] p-2 rounded space-y-1" data-testid="opportunity-card">
        <div className="flex items-start gap-2">
          {p.image && <img src={p.image} alt="" className="w-10 h-10 rounded object-cover shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold line-clamp-2 leading-tight">{p.title || p.handle}</div>
            <div className="text-[10px] data-number" style={{ color }}>
              {p.shopify_revenue != null && <>Org: {fmtMoney(p.shopify_revenue, currency)}</>}
              {p.ad_spend != null && p.ad_spend > 0 && <> · Ads: {fmtMoney(p.ad_spend, currency)}</>}
              {p.roas != null && <> · {p.roas}× ROAS</>}
            </div>
          </div>
        </div>
        <div className="text-[10px] text-[#A1A1AA]">{p.reason}</div>
        <div className="text-[10px]"><span className="text-[#71717A]">→ </span><span style={{ color }}>{p.action}</span></div>
      </div>
    ))}
  </div>
);

// =================== Creative Learnings ===================
const CreativeLearnings = ({ data, currency }) => {
  if (!data) return null;
  const { by_type = [], by_market = [] } = data;
  if (by_type.length === 0 && by_market.length === 0) return null;
  return (
    <Section
      title="Creative Learnings"
      subtitle="Performance patterns by format and market"
      icon={Sparkle}
      color="#A855F7"
      testid="creative-learnings-section"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LearningTable title="By Creative Format" rows={by_type} currency={currency} color="#06B6D4" />
        <LearningTable title="By Market / Audience" rows={by_market} currency={currency} color="#EC4899" />
      </div>
    </Section>
  );
};

const LearningTable = ({ title, rows, currency, color }) => (
  <div>
    <div className="text-xs font-semibold mb-2" style={{ color }}>{title}</div>
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] table-dense">
        <thead>
          <tr className="text-[#71717A] text-left">
            <th className="py-1 px-1">Group</th>
            <th className="py-1 px-1 text-right">Spend</th>
            <th className="py-1 px-1 text-right">Revenue</th>
            <th className="py-1 px-1 text-right">ROAS</th>
            <th className="py-1 px-1 text-right">CTR</th>
            <th className="py-1 px-1 text-right">Ads</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-[#1F1F22]">
              <td className="py-1 px-1 font-semibold">{r.name}</td>
              <td className="py-1 px-1 text-right data-number">{fmtMoney(r.spend, currency)}</td>
              <td className="py-1 px-1 text-right data-number" style={{ color: "#86EFAC" }}>{fmtMoney(r.revenue, currency)}</td>
              <td className="py-1 px-1 text-right data-number" style={{ color: r.roas >= 2 ? "#86EFAC" : r.roas >= 1 ? "#FCD34D" : "#FF9999" }}>{r.roas}×</td>
              <td className="py-1 px-1 text-right data-number">{r.ctr}%</td>
              <td className="py-1 px-1 text-right data-number">{r.ads}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// =================== Rules Modal ===================
const RULE_FIELDS = [
  { key: "min_roas_to_scale", label: "Min ROAS to Scale", help: "ROAS ≥ this triggers Scale Now" },
  { key: "scale_spend_min", label: "Min Spend to Scale (₹)", help: "Won't scale ads below this spend" },
  { key: "min_orders_for_winner", label: "Min Orders for Winner", help: "Below this stays in Monitor" },
  { key: "pause_spend_min", label: "Min Spend to Pause (₹)", help: "Spend threshold before pause is recommended" },
  { key: "monitor_min_ctr", label: "Min CTR to Monitor (%)", help: "Strong CTR floor for Monitor bucket" },
  { key: "min_clicks_for_landing_check", label: "Min Clicks for Landing Issue", help: "Clicks needed before flagging PDP issue" },
  { key: "tracking_clicks_min", label: "Tracking Issue Clicks", help: "Click count above which 0 orders = Pixel issue" },
  { key: "creative_refresh_ctr_drop_pct", label: "CTR Drop for Refresh (%)", help: "% drop vs prev period to trigger refresh" },
  { key: "min_freq_for_fatigue", label: "Frequency for Fatigue", help: "Frequency × above which creative is fatigued" },
  { key: "needs_mapping_spend_min", label: "Min Spend for Mapping Flag (₹)", help: "Below this, unmapped ads are ignored" },
];

const RulesModal = ({ clientId, open, onClose, onSaved }) => {
  const [rules, setRules] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/clients/${clientId}/performance-rules`)
      .then((r) => {
        setRules(r.data.rules || {});
        setDefaults(r.data.defaults || {});
      })
      .finally(() => setLoading(false));
  }, [open, clientId]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}/performance-rules`, { rules });
      onSaved?.();
      onClose();
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset to default rules?")) return;
    setSaving(true);
    try {
      const r = await axios.delete(`${API}/clients/${clientId}/performance-rules`);
      setRules(r.data.rules);
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="rules-modal">
      <div className="card-flat max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A] sticky top-0 bg-[#141414]">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Gear size={16} weight="bold" style={{ color: "#FFEB00" }} />
            Performance Rules
          </h3>
          <button onClick={onClose} className="text-[#71717A] hover:text-white" data-testid="rules-modal-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-[11px] text-[#71717A]">Tweak the thresholds the classifier uses for this client. Reset to defaults anytime.</p>
          {loading ? <div className="text-xs text-[#71717A] py-6 text-center">Loading…</div> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {RULE_FIELDS.map((f) => (
                <div key={f.key} className="space-y-1">
                  <label className="text-[10px] text-[#A1A1AA] flex items-center justify-between">
                    {f.label}
                    <span className="text-[#52525B]">default {defaults[f.key]}</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={rules[f.key] ?? ""}
                    onChange={(e) => setRules({ ...rules, [f.key]: e.target.value === "" ? null : parseFloat(e.target.value) })}
                    className="w-full bg-[#1A1A1A] border border-[#2A2A2A] text-white text-xs py-1.5 px-2 rounded"
                    data-testid={`rule-input-${f.key}`}
                  />
                  <div className="text-[9px] text-[#52525B]">{f.help}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between p-4 border-t border-[#2A2A2A] gap-2 sticky bottom-0 bg-[#141414]">
          <button onClick={reset} className="text-[11px] text-[#71717A] hover:text-white underline" data-testid="rules-reset-btn">
            Reset to defaults
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded bg-[#2A2A2A] hover:bg-[#3A3A3A]">Cancel</button>
            <button
              onClick={save}
              disabled={saving || loading}
              className="text-xs font-semibold px-3 py-1.5 rounded"
              style={{ background: "#FFEB00", color: "#000" }}
              data-testid="rules-save-btn"
            >
              {saving ? "Saving…" : "Save Rules"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================== AI Briefing ===================
const AiBriefing = ({ summary, loading, onGenerate }) => {
  // Render markdown lightly: convert ##/#/lists/bold to JSX
  const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    const nodes = [];
    let listBuf = [];
    const flush = () => {
      if (listBuf.length) {
        nodes.push(<ul key={`ul-${nodes.length}`} className="list-disc pl-5 space-y-1 my-1">{listBuf}</ul>);
        listBuf = [];
      }
    };
    const inline = (s) => {
      // **bold** → <strong>
      const parts = s.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**")
          ? <strong key={i} className="text-[#FFEB00]">{p.slice(2, -2)}</strong>
          : <span key={i}>{p}</span>
      );
    };
    lines.forEach((raw, idx) => {
      const line = raw.trimEnd();
      if (line.startsWith("### ")) { flush(); nodes.push(<h4 key={idx} className="text-sm font-bold mt-3 mb-1" style={{ color: "#A855F7" }}>{inline(line.slice(4))}</h4>); }
      else if (line.startsWith("## ")) { flush(); nodes.push(<h3 key={idx} className="text-base font-bold mt-3 mb-1" style={{ color: "#A855F7" }}>{inline(line.slice(3))}</h3>); }
      else if (line.startsWith("# ")) { flush(); nodes.push(<h2 key={idx} className="text-lg font-bold mt-3 mb-1" style={{ color: "#A855F7" }}>{inline(line.slice(2))}</h2>); }
      else if (line.match(/^[-*]\s/)) { listBuf.push(<li key={idx}>{inline(line.replace(/^[-*]\s/, ""))}</li>); }
      else if (line.trim() === "") { flush(); }
      else { flush(); nodes.push(<p key={idx} className="text-[12px] leading-relaxed">{inline(line)}</p>); }
    });
    flush();
    return nodes;
  };

  return (
    <div className="card-flat p-4 space-y-3" style={{ background: "linear-gradient(135deg, #A855F711, #06B6D411)", border: "1px solid #A855F733" }} data-testid="ai-briefing">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Sparkle size={16} weight="fill" style={{ color: "#A855F7" }} />
          AI Briefing
          <span className="text-[9px] font-normal text-[#71717A] bg-black/30 px-1.5 py-0.5 rounded">Claude Sonnet 4.5</span>
        </h3>
        <button
          onClick={onGenerate}
          disabled={loading}
          className="text-[10px] font-semibold px-3 py-1.5 rounded flex items-center gap-1.5"
          style={{ background: loading ? "#52525B" : "#A855F7", color: "#fff" }}
          data-testid="generate-ai-summary-btn"
        >
          <Sparkle size={11} weight="bold" className={loading ? "animate-pulse" : ""} />
          {loading ? "Thinking…" : summary ? "Regenerate" : "Generate Briefing"}
        </button>
      </div>
      {summary ? (
        <div className="text-[#E4E4E7] text-[12px]" data-testid="ai-briefing-content">
          {renderMarkdown(summary)}
        </div>
      ) : (
        <div className="text-[11px] text-[#A1A1AA]">
          Get a CMO-ready 200-word briefing of this analysis: TL;DR, wins to scale, leaks to fix, and recommended actions for the week.
        </div>
      )}
    </div>
  );
};

// =================== Main Component ===================
const PerformanceIntelligencePanel = ({ clientId, currency = "INR", integrationStatus, month: initialMonth }) => {
  const [days, setDays] = useState(30);
  const [month, setMonth] = useState(initialMonth || null);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rebuildingMappings, setRebuildingMappings] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(null);
  const writeEnabled = integrationStatus?.facebook?.permission_level === "write_enabled";

  // Build last-12-months option list (newest first), include the initial month if older
  const monthOptions = (() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("en-US", { month: "short", year: "numeric" });
      opts.push({ value: v, label });
    }
    if (initialMonth && !opts.find((o) => o.value === initialMonth)) {
      const [y, m] = initialMonth.split("-").map(Number);
      const d = new Date(y, m - 1, 1);
      opts.unshift({ value: initialMonth, label: d.toLocaleString("en-US", { month: "short", year: "numeric" }) });
    }
    return opts;
  })();

  // Sync local month with parent prop changes (top-bar month selector)
  useEffect(() => {
    if (initialMonth) setMonth(initialMonth);
  }, [initialMonth]);

  // Try to load cached analysis on mount / when days or month change
  const loadCached = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/performance-analysis/latest`, {
        params: { days, ...(month ? { month } : {}) },
      });
      if (res.data?.success) {
        setData(res.data);
        if (res.data.ai_summary) setAiSummary(res.data.ai_summary);
        else setAiSummary(null);
      } else {
        setData(null);
        setAiSummary(null);
      }
    } catch { /* ignore */ }
  }, [clientId, days, month]);

  useEffect(() => { loadCached(); }, [loadCached]);

  const runAnalysis = async () => {
    setRunning(true); setError(null); setAiSummary(null);
    try {
      // Kick off the job
      const start = await axios.post(`${API}/clients/${clientId}/performance-analysis`, null, {
        params: { days, ...(month ? { month } : {}) },
        timeout: 30000,
      });
      if (!start.data?.success) {
        setError(start.data?.message || "Could not start analysis.");
        setRunning(false);
        return;
      }
      // No job_id → empty-state response (no_cached_meta etc.)
      if (!start.data.job_id) {
        setRunning(false);
        if (start.data.error) setError(start.data.message);
        return;
      }

      // Poll the job — up to ~5 min, every 3s
      const jobId = start.data.job_id;
      const maxAttempts = 100; // 5 minutes
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const poll = await axios.get(`${API}/clients/${clientId}/performance-analysis/job/${jobId}`, { timeout: 15000 });
          const st = poll.data?.status;
          if (st === "done") {
            setData(poll.data);
            setRunning(false);
            return;
          }
          if (st === "failed") {
            setError(`Analysis failed: ${poll.data?.error || "unknown error"}`);
            setRunning(false);
            return;
          }
        } catch (pe) {
          // Transient poll failures shouldn't kill the loop; only fail after sustained issues
          if (i > 5 && pe.message) {
            setError(`Polling failed: ${pe.message}. The analysis may still be running — try refreshing in a minute.`);
            setRunning(false);
            return;
          }
        }
      }
      setError("Analysis is taking longer than 5 minutes. Try a smaller period (7 days) or check support.");
      setRunning(false);
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.message;
      const status = e.response?.status;
      setError(
        status
          ? `Server returned ${status}: ${detail || e.message}`
          : `${e.message || "Network error"} — could not start analysis.`
      );
      setRunning(false);
    }
  };

  const generateAiSummary = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/performance-analysis/ai-summary`, { days }, { timeout: 90000 });
      if (res.data?.success) {
        setAiSummary(res.data.summary);
      } else {
        alert(res.data?.message || "AI summary failed");
      }
    } catch (e) {
      alert("AI summary failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setAiLoading(false);
    }
  };

  const handleQuickAction = async (ad, kind) => {
    if (!window.confirm(`${kind === "pause" ? "Pause" : "Resume"} ad "${ad.name}"?`)) return;
    try {
      await axios.post(`${API}/clients/${clientId}/facebook/ad-action`, {
        ad_id: ad.id,
        action: kind,
        ad_name: ad.name,
      });
      await runAnalysis();
    } catch (e) {
      alert("Action failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleBulkAction = async (bucketKey, bucketAds, kind) => {
    if (!writeEnabled) { alert("Meta write permission required."); return; }
    if (bucketAds.length === 0) return;
    if (!window.confirm(`${kind === "pause" ? "Pause" : "Resume"} all ${bucketAds.length} ads in "${BUCKET_META[bucketKey].label}"?`)) return;
    setBulkRunning(bucketKey);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/facebook/ad-action/bulk`, {
        ad_ids: bucketAds.map((a) => a.id),
        action: kind,
        ad_names: Object.fromEntries(bucketAds.map((a) => [a.id, a.name])),
      });
      alert(`${kind === "pause" ? "Paused" : "Resumed"} ${res.data.ok}/${res.data.total} ads.${res.data.failed ? ` ${res.data.failed} failed.` : ""}`);
      await runAnalysis();
    } catch (e) {
      alert("Bulk action failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBulkRunning(null);
    }
  };

  const handleRebuildMappings = async () => {
    if (!summary?.current_months) return;
    setRebuildingMappings(true);
    try {
      // Fire mapping rebuild for each month in scope (latest first)
      for (const m of summary.current_months) {
        await axios.post(`${API}/clients/${clientId}/shopify/build-shopify-mappings?month=${m}`, {}, { timeout: 180000 });
      }
      await runAnalysis();
    } catch (e) {
      alert("Mapping rebuild failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setRebuildingMappings(false);
    }
  };

  const summary = data?.summary;
  const ads = data?.ads || [];

  return (
    <div className="space-y-4" data-testid="performance-intelligence">
      {/* Launcher */}
      <div className="card-flat p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Sparkle size={16} weight="fill" style={{ color: "#FFEB00" }} />
            Performance Intelligence Engine
          </h3>
          <p className="text-[11px] text-[#71717A] mt-0.5">
            Analyses Meta + Shopify together. Classifies every ad into a clear next action.
            {month && <span className="ml-1 text-[#FFEB00]">· Anchored to {month}</span>}
          </p>
        </div>
        <select
          value={month || ""}
          onChange={(e) => setMonth(e.target.value || null)}
          className="bg-[#1A1A1A] border border-[#2A2A2A] text-white text-xs py-2 px-3 rounded"
          data-testid="perf-month-select"
          title="Anchor month — analysis covers trailing days ending in this month"
        >
          {monthOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="bg-[#1A1A1A] border border-[#2A2A2A] text-white text-xs py-2 px-3 rounded"
          data-testid="perf-period-select"
        >
          {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button
          onClick={() => setRulesOpen(true)}
          className="px-3 py-2 rounded text-xs flex items-center gap-1.5 border border-[#2A2A2A] hover:bg-[#1A1A1A]"
          title="Edit performance thresholds"
          data-testid="perf-rules-btn"
        >
          <Gear size={14} weight="bold" />
          Rules
        </button>
        <button
          onClick={runAnalysis}
          disabled={running}
          className="px-4 py-2 rounded font-semibold text-xs flex items-center gap-2 transition-colors"
          style={{ background: running ? "#52525B" : "#FFEB00", color: "#000" }}
          data-testid="run-perf-analysis-btn"
        >
          <ArrowsClockwise size={14} className={running ? "animate-spin" : ""} weight="bold" />
          {running ? "Analysing…" : "Run Performance Analysis"}
        </button>
      </div>

      <RulesModal clientId={clientId} open={rulesOpen} onClose={() => setRulesOpen(false)} onSaved={runAnalysis} />

      {error && (
        <div className="card-flat p-3 text-xs flex items-center gap-2" style={{ background: "#EF444411", color: "#FF9999" }}>
          <Warning size={14} />
          {error}
        </div>
      )}

      {!summary && !error && !running && (
        <div className="card-flat p-8 text-center">
          <ChartLineUp size={32} className="mx-auto text-[#52525B] mb-2" />
          <div className="text-sm text-[#A1A1AA]">No analysis yet for this period.</div>
          <div className="text-[11px] text-[#71717A] mt-1">Pick a window and click <span className="font-semibold text-[#FFEB00]">Run Performance Analysis</span> to start.</div>
        </div>
      )}

      {summary && (
        <>
          <DecisionSummary summary={summary} currency={currency} onRebuildMappings={handleRebuildMappings} rebuildingMappings={rebuildingMappings} />

          {/* AI Briefing — Claude-generated CMO summary */}
          <AiBriefing summary={aiSummary} loading={aiLoading} onGenerate={generateAiSummary} />

          <WhatToDoToday summary={summary} ads={ads} currency={currency} />

          {/* Action Buckets */}
          {Object.entries(BUCKET_META)
            .sort((a, b) => a[1].priority - b[1].priority)
            .map(([key, meta]) => {
              const bucketAds = ads.filter((a) => a.classification?.bucket === key);
              if (bucketAds.length === 0) return null;
              const canBulkPause = writeEnabled && (key === "pause" || key === "refresh_creative");
              const canBulkResume = writeEnabled && key === "scale";
              return (
                <div key={key} id={`bucket-${key}`}>
                  <Section
                    title={meta.label}
                    subtitle={`${bucketAds.length} ad${bucketAds.length > 1 ? "s" : ""} · ${fmtMoney(summary.bucket_spend?.[key] || 0, currency)} spend`}
                    icon={meta.icon}
                    color={meta.color}
                    count={bucketAds.length}
                    defaultOpen={meta.priority <= 2}
                    testid={`bucket-section-${key}`}
                  >
                    {(canBulkPause || canBulkResume) && (
                      <div className="mb-3 flex items-center gap-2">
                        <button
                          onClick={() => handleBulkAction(key, bucketAds, canBulkPause ? "pause" : "resume")}
                          disabled={bulkRunning === key}
                          className="text-[10px] font-semibold px-2.5 py-1.5 rounded flex items-center gap-1.5"
                          style={{ background: `${meta.color}22`, color: meta.color }}
                          data-testid={`bulk-${key}-btn`}
                        >
                          {canBulkPause ? <Pause size={11} weight="bold" /> : <Play size={11} weight="bold" />}
                          {bulkRunning === key
                            ? "Running…"
                            : `${canBulkPause ? "Pause" : "Resume"} all ${bucketAds.length} ads`}
                        </button>
                        <span className="text-[10px] text-[#71717A]">All actions audited individually.</span>
                      </div>
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {bucketAds.map((ad) => (
                        <AdCard key={ad.id} ad={ad} currency={currency} onAction={handleQuickAction} writeEnabled={writeEnabled} />
                      ))}
                    </div>
                  </Section>
                </div>
              );
            })}

          {/* Product Opportunities (Stage 2) */}
          <ProductOpportunities data={data?.product_opportunities} currency={currency} />

          {/* Creative Learnings (Stage 2) */}
          <CreativeLearnings data={data?.creative_learnings} currency={currency} />

          {/* Detailed Audit Table — collapsed by default */}
          <Section
            title="Detailed Audit Table"
            subtitle={`All ${ads.length} ads · sortable by spend`}
            icon={ShoppingBag}
            color="#71717A"
            defaultOpen={false}
            testid="audit-table-section"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] table-dense">
                <thead>
                  <tr className="text-left text-[#71717A]">
                    <th className="py-1 px-2">Ad</th>
                    <th className="py-1 px-2 text-right">Spend</th>
                    <th className="py-1 px-2 text-right">Revenue</th>
                    <th className="py-1 px-2 text-right">ROAS</th>
                    <th className="py-1 px-2 text-right">CTR</th>
                    <th className="py-1 px-2 text-right">Orders</th>
                    <th className="py-1 px-2">Bucket</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.slice().sort((a, b) => (b.metrics?.spend || 0) - (a.metrics?.spend || 0)).map((ad) => {
                    const m = ad.metrics || {};
                    const sh = ad.shopify || {};
                    const meta = BUCKET_META[ad.classification?.bucket];
                    return (
                      <tr key={ad.id} className="border-t border-[#1F1F22]">
                        <td className="py-1 px-2 max-w-[260px] truncate">{ad.name}</td>
                        <td className="py-1 px-2 text-right data-number">{fmtMoney(m.spend, currency)}</td>
                        <td className="py-1 px-2 text-right data-number" style={{ color: "#86EFAC" }}>{fmtMoney(sh.revenue, currency)}</td>
                        <td className="py-1 px-2 text-right data-number">{sh.roas || 0}×</td>
                        <td className="py-1 px-2 text-right data-number">{m.ctr || 0}%</td>
                        <td className="py-1 px-2 text-right data-number">{fmtNum(sh.orders)}</td>
                        <td className="py-1 px-2">
                          {meta ? (
                            <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${meta.color}22`, color: meta.color }}>
                              {meta.label}
                            </span>
                          ) : <span className="text-[#52525B]">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </div>
  );
};

export default PerformanceIntelligencePanel;
